"use client";

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { getUserFollowedCompaniesAction, updateUserFollowedCompaniesAction } from '@/app/actions/vacancySyncActions';


export interface SubscribedCompany {
  id?: number | string;
  name: string;
  logo?: string;
  location?: string;
  logoColor?: string;
  subscribedAt: string;
}

interface SubscribedCompaniesContextType {
  subscribedCompanies: SubscribedCompany[];
  subscribe: (company: Omit<SubscribedCompany, 'subscribedAt'>) => void;
  unsubscribe: (companyName: string, companyId?: number | string) => void;
  isSubscribed: (companyName: string) => boolean;
}

const SubscribedCompaniesContext = createContext<SubscribedCompaniesContextType | undefined>(undefined);

export function SubscribedCompaniesProvider({ children }: { children: React.ReactNode }) {
  const [subscribedCompanies, setSubscribedCompanies] = useState<SubscribedCompany[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const userIdRef = useRef<string>('guest');
  const { data: session, status } = useSession();


  const getDynamicStorageKey = () => {
    if (typeof window === 'undefined') return 'myjobs_subscribed_companies_guest';
    const emailMatch = document.cookie.match(/(^| )userEmail=([^;]+)/);
    const storedEmail = localStorage.getItem('userEmail') || (emailMatch ? decodeURIComponent(emailMatch[2]) : null);
    const isAuth = localStorage.getItem('isLoggedIn') === 'true' || !!storedEmail;
    const idMatch = document.cookie.match(/(^| )wpUserId=([^;]+)/);
    const fallbackId = localStorage.getItem('wpUserId') || (idMatch ? idMatch[2] : null);
    const userIdentifier = storedEmail 
      ? `email_${storedEmail.toLowerCase().trim().replace(/[^a-z0-9]/g, '_')}` 
      : (fallbackId ? `id_${fallbackId}` : (isAuth ? 'user' : 'guest'));
    return `myjobs_subscribed_companies_${userIdentifier}`;
  };

  // Strip heavy fields (logo base64) before saving to localStorage to avoid QuotaExceededError
  // We cache the logo in sessionStorage so it doesn't flash when changing language/reloading
  const slimForStorage = (companies: SubscribedCompany[]) => {
    if (typeof window !== 'undefined') {
      companies.forEach(c => {
        if (c.logo) {
          try { sessionStorage.setItem(`myjobs_logo_${c.name}`, c.logo); } catch(e){}
        }
      });
    }
    return companies.map(({ logo: _logo, logoColor: _lc, ...rest }) => rest);
  };

  const syncToWP = async (followed: SubscribedCompany[]) => {

    if (typeof window === 'undefined') return;
    const emailMatch = document.cookie.match(/(^| )userEmail=([^;]+)/);
    const email = localStorage.getItem('userEmail') || (emailMatch ? decodeURIComponent(emailMatch[2]) : null);
    const idMatch = document.cookie.match(/(^| )wpUserId=([^;]+)/);
    const wpId = localStorage.getItem('wpUserId') || (idMatch ? idMatch[2] : null);
    if (email || wpId) {
      try {
        fetch('https://admin.myjobs.al/wp-json/myjobs/v1/user-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: wpId,
            user_email: email,
            followed_companies: followed
          })
        }).catch(console.error);
      } catch (e) {}

      await updateUserFollowedCompaniesAction(followed, email || undefined, wpId || undefined);
    }
  };

  useEffect(() => {
    setIsMounted(true);

    const emailMatch = document.cookie.match(/(^| )userEmail=([^;]+)/);
    const storedEmail = localStorage.getItem('userEmail') || (emailMatch ? decodeURIComponent(emailMatch[2]) : null);
    const isAuth = localStorage.getItem('isLoggedIn') === 'true' || !!storedEmail;
    
    const idMatch = document.cookie.match(/(^| )wpUserId=([^;]+)/);
    const fallbackId = localStorage.getItem('wpUserId') || (idMatch ? idMatch[2] : null);
    
    const userIdentifier = storedEmail 
      ? `email_${storedEmail.toLowerCase().trim().replace(/[^a-z0-9]/g, '_')}` 
      : (fallbackId ? `id_${fallbackId}` : (isAuth ? 'user' : 'guest'));

    userIdRef.current = userIdentifier;
    const key = `myjobs_subscribed_companies_${userIdentifier}`;
    
    // If not logged in (guest), do not show any followed companies
    if (userIdentifier === 'guest') {
      setSubscribedCompanies([]);
    } else {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const hydrated = parsed.map((c: SubscribedCompany) => {
            if (typeof window !== 'undefined') {
              const cachedLogo = sessionStorage.getItem(`myjobs_logo_${c.name}`);
              if (cachedLogo) c.logo = cachedLogo;
            }
            return c;
          });
          setSubscribedCompanies(hydrated);
        } catch (e) {
          console.error("Failed to parse Followed companies", e);
        }
      } else {
        setSubscribedCompanies([]);
      }

      // Asynchronously fetch from WP if logged in
      const email = storedEmail || undefined;
      const wpId = fallbackId || undefined;

      const queryParts = [];
      if (wpId) queryParts.push(`user_id=${encodeURIComponent(wpId)}`);
      if (email) queryParts.push(`user_email=${encodeURIComponent(email)}`);

      const endpoint = `https://admin.myjobs.al/wp-json/myjobs/v1/user-data?${queryParts.join('&')}`;
      fetch(endpoint, { cache: 'no-store' })
        .then(r => r.json())
        .then(cData => {
          if (cData && cData.followed_companies !== undefined) {
            let followed = cData.followed_companies;
            if (typeof followed === 'string' && followed.trim() !== '') {
              try { followed = JSON.parse(followed); } catch (e) {}
            }
            const raw = Array.isArray(followed) ? followed : [];
            const wpFollowed: SubscribedCompany[] = raw.map((item: any) => {
              let comp = typeof item === 'string' ? { name: item, subscribedAt: new Date().toISOString() } : { ...item };
              if (comp.logo && typeof window !== 'undefined') {
                try { sessionStorage.setItem(`myjobs_logo_${comp.name}`, comp.logo); } catch(e){}
              }
              if (!comp.logo && typeof window !== 'undefined') {
                const cachedLogo = sessionStorage.getItem(`myjobs_logo_${comp.name}`);
                if (cachedLogo) comp.logo = cachedLogo;
              }
              return comp;
            });
            setSubscribedCompanies(wpFollowed);
            localStorage.setItem(key, JSON.stringify(slimForStorage(wpFollowed)));
          }
        })
        .catch(console.error);
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'wpUserId' || e.key === 'isLoggedIn') {
        window.location.reload();
      }
      if (e.key === key) {
        if (e.newValue) {
          try {
            const parsed = JSON.parse(e.newValue);
            const hydrated = parsed.map((c: SubscribedCompany) => {
              if (typeof window !== 'undefined') {
                const cachedLogo = sessionStorage.getItem(`myjobs_logo_${c.name}`);
                if (cachedLogo) c.logo = cachedLogo;
              }
              return c;
            });
            setSubscribedCompanies(hydrated);
          } catch (e) {}
        } else {
          setSubscribedCompanies([]);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Session effect: load data for OAuth (Google/Facebook) users whose email isn't yet in localStorage
  useEffect(() => {
    if (status !== 'authenticated' || !session) return;
    if (typeof window !== 'undefined' && sessionStorage.getItem('logging_out') === 'true') return;
    const sessionEmail = session.user?.email;
    const sessionWpId = String((session as any).wpUserId ?? '');
    if (!sessionEmail) return;

    // Save to localStorage so future refreshes are instant
    if (!localStorage.getItem('userEmail')) localStorage.setItem('userEmail', sessionEmail);
    if (!localStorage.getItem('isLoggedIn')) localStorage.setItem('isLoggedIn', 'true');
    if (sessionWpId && !localStorage.getItem('wpUserId')) localStorage.setItem('wpUserId', sessionWpId);

    // Only fetch if we currently show an empty list (would mean localStorage was empty)
    if (subscribedCompanies.length > 0) return;

    const safeEmail = sessionEmail.toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
    const key = `myjobs_subscribed_companies_email_${safeEmail}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const hydrated = parsed.map((c: SubscribedCompany) => {
          if (typeof window !== 'undefined') {
            const cachedLogo = sessionStorage.getItem(`myjobs_logo_${c.name}`);
            if (cachedLogo) c.logo = cachedLogo;
          }
          return c;
        });
        setSubscribedCompanies(hydrated);
        return;
      } catch (e) {}
    }

    const idPart = sessionWpId ? `user_id=${sessionWpId}` : `user_email=${encodeURIComponent(sessionEmail)}`;
    fetch(`https://admin.myjobs.al/wp-json/myjobs/v1/user-data?${idPart}`, { cache: 'no-store' })
      .then(r => r.json())
      .then(cData => {
        if (!cData?.followed_companies) return;
        let followed = cData.followed_companies;
        if (typeof followed === 'string' && followed.trim()) {
          try { followed = JSON.parse(followed); } catch (e) {}
        }
        const raw = Array.isArray(followed) ? followed : [];
        const wpFollowed: SubscribedCompany[] = raw.map((item: any) => {
          let comp = typeof item === 'string' ? { name: item, subscribedAt: new Date().toISOString() } : { ...item };
          if (comp.logo && typeof window !== 'undefined') {
            try { sessionStorage.setItem(`myjobs_logo_${comp.name}`, comp.logo); } catch(e){}
          }
          if (!comp.logo && typeof window !== 'undefined') {
            const cachedLogo = sessionStorage.getItem(`myjobs_logo_${comp.name}`);
            if (cachedLogo) comp.logo = cachedLogo;
          }
          return comp;
        });
        setSubscribedCompanies(wpFollowed);
        localStorage.setItem(key, JSON.stringify(slimForStorage(wpFollowed)));
      })
      .catch(console.error);
  }, [status, session]);

  const subscribe = (company: Omit<SubscribedCompany, 'subscribedAt'>) => {
    const newCompany: SubscribedCompany = {
      ...company,
      subscribedAt: new Date().toISOString()
    };
    const currentKey = getDynamicStorageKey();

    setSubscribedCompanies((prev) => {
      if (prev.some(c => c.name.toLowerCase() === company.name.toLowerCase())) return prev;
      const updated = [...prev, newCompany];
      try { localStorage.setItem(currentKey, JSON.stringify(slimForStorage(updated))); } catch (e) { console.warn('localStorage full, skipping cache'); }
      syncToWP(updated);
      return updated;
    });


    const compId = company.id ? Number(company.id) : undefined;
    import('@/app/actions/companyActions').then(({ followCompany }) => {
      followCompany(company.name, compId);
    }).catch(console.error);
  };

  const unsubscribe = (companyName: string, companyId?: number | string) => {
    const currentKey = getDynamicStorageKey();
    setSubscribedCompanies((prev) => {
      const updated = prev.filter(c => c.name.toLowerCase() !== companyName.toLowerCase());
      try { localStorage.setItem(currentKey, JSON.stringify(slimForStorage(updated))); } catch (e) { console.warn('localStorage full, skipping cache'); }

      syncToWP(updated);
      return updated;
    });

    const compId = companyId ? Number(companyId) : undefined;
    import('@/app/actions/companyActions').then(({ unfollowCompany }) => {
      unfollowCompany(companyName, compId);
    }).catch(console.error);
  };

  const isSubscribed = (companyName: string) => {
    return subscribedCompanies.some(c => c.name.toLowerCase() === companyName.toLowerCase());
  };

  return (
    <SubscribedCompaniesContext.Provider value={{ subscribedCompanies, subscribe, unsubscribe, isSubscribed }}>
      {children}
    </SubscribedCompaniesContext.Provider>
  );
}

export function useSubscribedCompanies() {
  const context = useContext(SubscribedCompaniesContext);
  if (context === undefined) {
    throw new Error('useSubscribedCompanies must be used within a SubscribedCompaniesProvider');
  }
  return context;
}


